import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap,
  Mail,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  CloudDownload,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Attached hero asset — moody dark car with red LED taillight (right-side)
const CAR_HERO_URL =
  "https://customer-assets.emergentagent.com/job_58b68a77-750a-4361-b8c9-05ffe89552ef/artifacts/hqr8gbr8_5D40FF44-436A-4C95-969A-3B2A8A198CA1.png";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const [mode, setMode] = useState("choose"); // choose | email | sent
  const [email, setEmail] = useState("");
  const [emailIntent, setEmailIntent] = useState("signin"); // signin | signup — copy-only distinction
  const [submitting, setSubmitting] = useState(false);

  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  const openEmail = (intent) => {
    setEmailIntent(intent);
    setMode("email");
  };

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    const clean = (email || "").trim().toLowerCase();
    if (!clean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await axios.post(
        `${API}/api/auth/magic-link/request`,
        {
          email: clean,
          redirect_to: "/dashboard",
          origin: window.location.origin,
        },
        { withCredentials: true }
      );
      setMode("sent");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "We couldn't send your sign-in link. Please try again in a moment.";
      toast.error(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-theme="dark"
      className="min-h-screen bg-[var(--bg-deep)] text-[var(--text-primary)] relative overflow-hidden"
      data-testid="login-page"
    >
      {/* Decorative car — a single background layer, sized and positioned
          differently at each breakpoint. Mobile: a small tail-light peek in
          the top-right corner (~260px tall). Desktop: fills the right ~50%
          of the viewport but with reduced opacity + strong left-fade so it
          sits BEHIND the auth surface rather than competing with it.  */}
      <div
        className="absolute inset-0 pointer-events-none select-none"
        aria-hidden="true"
      >
        {/* Mobile version — corner peek */}
        <div
          className="lg:hidden absolute top-0 right-0 h-[240px] w-[70%]"
          style={{
            backgroundImage: `url('${CAR_HERO_URL}')`,
            backgroundSize: "cover",
            backgroundPosition: "left 30% center",
            backgroundRepeat: "no-repeat",
            opacity: 0.55,
            maskImage:
              "linear-gradient(to bottom left, black 20%, transparent 78%)",
            WebkitMaskImage:
              "linear-gradient(to bottom left, black 20%, transparent 78%)",
          }}
        />

        {/* Desktop version — right-half hero, blended */}
        <div
          className="hidden lg:block absolute top-0 right-0 h-full"
          style={{
            width: "min(56vw, 900px)",
            backgroundImage: `url('${CAR_HERO_URL}')`,
            backgroundSize: "cover",
            backgroundPosition: "left center",
            backgroundRepeat: "no-repeat",
            opacity: 0.72,
            maskImage:
              "linear-gradient(to right, transparent 0%, black 30%, black 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 30%, black 100%)",
          }}
        />

        {/* Ambient dark wash on top so the car doesn't over-brighten the
            page — keeps the car decorative not dominant. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 82% 55%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.8) 100%)",
          }}
        />

        {/* Subtle red glow near the taillight (both breakpoints) */}
        <div
          className="absolute opacity-60 hidden lg:block"
          style={{
            top: "48%",
            right: "22%",
            width: 260,
            height: 260,
            background:
              "radial-gradient(circle, rgba(232,33,39,0.28) 0%, rgba(232,33,39,0) 65%)",
          }}
        />
      </div>

      {/* Content — auth is the primary focus. Left-aligned column with a
          comfortable but not oversized max-width so the copy sits close to
          the auth card without a huge empty gutter on the left. */}
      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex-1 flex items-start lg:items-center px-6 lg:px-14 pt-8 lg:pt-0">
          <div className="w-full max-w-[440px] lg:max-w-[500px] fade-up">
            {/* Brand */}
            <div className="flex items-center gap-3 mb-8 lg:mb-10">
              <div
                className="relative w-10 h-10 bg-[var(--bg-inverse)] flex items-center justify-center"
                style={{ borderRadius: 2 }}
              >
                <Zap
                  className="w-5 h-5 text-[var(--text-inverse)]"
                  strokeWidth={2.25}
                />
                <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-[#E82127] rounded-full" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-lg font-medium tracking-tight">
                  MileageIQ
                </div>
                <div className="label-tiny">Console</div>
              </div>
            </div>

            {/* Hero copy */}
            <div className="mb-8 lg:mb-10">
              <h1 className="font-display text-[38px] md:text-5xl lg:text-[52px] font-light tracking-tighter leading-[1.03] mb-4">
                Sign in to.
                <br />
                <span className="text-[#E82127]">your garage.</span>
              </h1>
              <p className="text-sm md:text-[15px] text-[var(--text-secondary)] leading-relaxed">
                Smart Vehicle Analytics, mileage tracking, charging costs, fuel spend across your entire garage. Private to your account.
              </p>
            </div>

            {/* Auth card — a subtle panel, not a hard-bordered container.
                Single 1px border, no nested rounded chrome. */}
            <div
              className="bg-[#0A0A0B]/70 backdrop-blur-sm border border-[var(--border-subtle)]"
              style={{ borderRadius: 4 }}
              data-testid="login-card"
            >
              <div className="p-5 lg:p-6">
                {mode === "choose" && (
                  <ChooseMode
                    onGoogle={handleGoogleSignIn}
                    onEmail={() => openEmail("signin")}
                    onCreate={() => openEmail("signup")}
                  />
                )}
                {mode === "email" && (
                  <EmailForm
                    email={email}
                    setEmail={setEmail}
                    intent={emailIntent}
                    submitting={submitting}
                    onSubmit={submitEmail}
                    onBack={() => setMode("choose")}
                  />
                )}
                {mode === "sent" && (
                  <SentConfirmation
                    email={email}
                    onBack={() => {
                      setMode("choose");
                      setEmail("");
                    }}
                    onResend={submitEmail}
                    submitting={submitting}
                  />
                )}
              </div>
            </div>

            {/* Trust chips */}
            <TrustChips />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 lg:px-14 py-5 flex items-center justify-between text-[11px] text-[var(--text-faint)]">
          <span className="font-mono-data">v1.4 · auth gated</span>
          <span>Sessions expire after 7 days.</span>
        </div>
      </div>

      {/*
        Terms of Service · Privacy Policy — kept in code (commented) for a
        future surface once the pages exist. Per user request, hidden today.

        <p className="text-[11px] text-[var(--text-faint)] mt-4 text-center max-w-[460px]">
          By continuing you agree to our <a href="/terms" className="underline text-[#E82127]">Terms of Service</a> and <a href="/privacy" className="underline text-[#E82127]">Privacy Policy</a>.
        </p>
      */}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Sub-components                                                          */
/* ---------------------------------------------------------------------- */

function ChooseMode({ onGoogle, onEmail, onCreate }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 mb-1">
        <span className="flex-1 h-px bg-[var(--border-subtle)]" />
        <span className="label-tiny text-[var(--text-muted)]">Sign in</span>
        <span className="flex-1 h-px bg-[var(--border-subtle)]" />
      </div>

      {/* Google — high-contrast primary (white on dark canvas) */}
      <Button
        onClick={onGoogle}
        className="w-full bg-white text-[#0A0A0B] hover:bg-white/90 tap h-11 text-sm font-medium justify-center"
        style={{ borderRadius: 2 }}
        data-testid="google-signin-btn"
      >
        <GoogleIcon className="w-4 h-4 mr-2" />
        Continue with Google
      </Button>

      {/* Email — outlined secondary */}
      <Button
        onClick={onEmail}
        variant="outline"
        className="w-full bg-transparent text-[var(--text-primary)] border-[var(--border-hover)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)] tap h-11 text-sm font-medium justify-center"
        style={{ borderRadius: 2 }}
        data-testid="email-signin-btn"
      >
        <Mail className="w-4 h-4 mr-2" />
        Continue with Email
      </Button>

      {/* OR divider */}
      <div className="flex items-center gap-3 pt-2">
        <span className="flex-1 h-px bg-[var(--border-subtle)]" />
        <span className="label-tiny text-[var(--text-muted)]">Or</span>
        <span className="flex-1 h-px bg-[var(--border-subtle)]" />
      </div>

      {/* Create Account CTA — outlined red, mirrors mockup */}
      <div className="text-center pt-1">
        <div className="text-sm font-medium text-[var(--text-primary)]">
          New to MileageIQ?
        </div>
        <div className="text-xs text-[var(--text-secondary)] mb-3">
          Create an account to get started.
        </div>
        <button
          type="button"
          onClick={onCreate}
          className="tap w-full inline-flex items-center justify-center gap-2 border border-[#E82127] text-[#E82127] hover:bg-[#E82127]/10 h-10 text-sm font-medium transition-colors"
          style={{ borderRadius: 2 }}
          data-testid="create-account-btn"
        >
          Create Account <ArrowRight className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

function EmailForm({ email, setEmail, intent, submitting, onSubmit, onBack }) {
  const isSignup = intent === "signup";
  return (
    <form className="space-y-4" onSubmit={onSubmit} data-testid="email-form">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase text-[var(--text-muted)] hover:text-[var(--text-primary)] tap"
        data-testid="email-form-back"
      >
        <ArrowLeft className="w-3 h-3" strokeWidth={2} />
        Back
      </button>

      <div>
        <div className="label-tiny mb-1">
          {isSignup ? "Create your account" : "Sign in with email"}
        </div>
        <p className="text-xs text-[var(--text-secondary)] leading-snug">
          We&apos;ll email you a one-tap sign-in link. No password, no hassle.
        </p>
      </div>

      <div className="space-y-1.5">
        <label
          htmlFor="magic-email"
          className="label-tiny text-[var(--text-muted)]"
        >
          Email
        </label>
        <Input
          id="magic-email"
          type="email"
          autoComplete="email"
          autoFocus
          required
          placeholder="you@garage.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-11"
          data-testid="email-input"
          disabled={submitting}
        />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#E82127] hover:bg-[#C81C22] text-white tap h-11 text-sm font-medium justify-center"
        style={{ borderRadius: 2 }}
        data-testid="email-submit-btn"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Sending…
          </>
        ) : (
          <>
            {isSignup ? "Send link & create account" : "Send sign-in link"}
            <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </>
        )}
      </Button>
    </form>
  );
}

function SentConfirmation({ email, onBack, onResend, submitting }) {
  return (
    <div className="space-y-4 text-center py-1" data-testid="email-sent">
      <div className="mx-auto w-10 h-10 rounded-full border border-[#34D17A]/40 bg-[#34D17A]/10 flex items-center justify-center">
        <CheckCircle2 className="w-5 h-5 text-[#34D17A]" strokeWidth={1.75} />
      </div>
      <div>
        <div className="label-tiny mb-1">Check your inbox</div>
        <p className="text-sm text-[var(--text-primary)] font-medium mb-1 break-all">
          {email}
        </p>
        <p className="text-xs text-[var(--text-secondary)] leading-snug">
          We just sent you a sign-in link. It expires in 15 minutes and can only
          be used once. If it doesn&apos;t arrive, check your spam folder.
        </p>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="tap flex-1 h-10 border border-[var(--border-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] text-sm transition-colors"
          style={{ borderRadius: 2 }}
          data-testid="email-sent-back"
        >
          Use another
        </button>
        <button
          type="button"
          onClick={onResend}
          disabled={submitting}
          className="tap flex-1 h-10 bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:opacity-90 text-sm transition-opacity disabled:opacity-60"
          style={{ borderRadius: 2 }}
          data-testid="email-sent-resend"
        >
          {submitting ? "Sending…" : "Resend link"}
        </button>
      </div>
    </div>
  );
}

function TrustChips() {
  const items = [
    {
      icon: ShieldCheck,
      title: "Your data is private",
      body: "We never share your mileage or usage.",
    },
    {
      icon: CloudDownload,
      title: "Secure & trusted",
      body: "Data is encrypted and stored securely.",
    },
    {
      icon: Zap,
      title: "Built for drivers",
      body: "Designed for EV, hybrid and gas vehicles.",
    },
  ];
  return (
    <div className="mt-6 lg:mt-8 grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-x-4 lg:gap-y-0">
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <div
            key={i}
            className="flex items-start gap-2.5"
            data-testid={`trust-chip-${i}`}
          >
            <Icon
              className="w-4 h-4 mt-0.5 text-[#E82127] shrink-0"
              strokeWidth={1.75}
            />
            <div className="leading-snug">
              <div className="text-[12px] font-medium text-[var(--text-primary)]">
                {it.title}
              </div>
              <div className="text-[11px] text-[var(--text-muted)]">
                {it.body}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GoogleIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 7.7-11.3 7.7-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 2.9l5.7-5.7C34 5.1 29.3 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21c11.6 0 20.7-8.4 20.7-21 0-1.2-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13.3 24 13.3c3 0 5.8 1.1 7.9 2.9l5.7-5.7C34 5.1 29.3 3 24 3 16.1 3 9.3 7.5 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 45c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.3 0-9.7-3.2-11.3-7.6l-6.5 5C9.1 40.4 16 45 24 45z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.2c-.4.4 6.5-4.7 6.5-14.7 0-1.2-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
